/**
 * cart-normalizer-theme-only.js
 *
 * Classifies each cart variant using quantity math + pickup availability:
 *
 *   totalQty      = ProductVariant.quantityAvailable (all sellable stock, all locations)
 *   pickupQty     = sum of storeAvailability[].quantityAvailable (retail locations)
 *   warehouseQty  = totalQty - pickupQty
 *   pickupAvail   = any storeAvailability node has available === true
 *
 *   warehouseQty > 0                -> SHIPPABLE (has courier/warehouse stock)
 *   warehouseQty > 0 && !pickupAvail -> SHIPPING-ONLY (shippable, but no pickup at any store)
 *   warehouseQty <= 0               -> PICKUP-ONLY (all stock is at retail locations)
 *
 * The modal is shown only when SHIPPING-ONLY items coexist with PICKUP-ONLY items,
 * because there is no single fulfillment method that covers both.
 * A cart mixing SHIPPABLE+PICKUP items with PICKUP-ONLY items is fine — everything
 * can be picked up, so checkout proceeds normally.
 */
(function () {
  'use strict';

  // Config

  var configEl = document.getElementById('cart-normalizer-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent.trim());
  } catch (e) {
    console.error(e);
    return;
  }

  var shop  = config.shop;
  var token = config.storefrontToken;

  if (!shop || !token) {
    return;
  }

  var pendingPickupItems = null;

  // Storefront API query
  //
  // quantityAvailable on the variant = total sellable stock across ALL locations.
  // quantityAvailable on each storeAvailability node = stock at that pickup location.
  // Difference = stock at warehouse/courier locations (no pickup enabled there).

  function buildQuery() {
    return (
      'query($ids:[ID!]!) {' +
        'nodes(ids:$ids) {' +
          '... on ProductVariant {' +
            'id ' +
            'availableForSale ' +
            'quantityAvailable ' +
            'product { title } ' +
            'storeAvailability(first:100) {' +
              'nodes { available quantityAvailable location { id name } }' +
            '}' +
          '}' +
        '}' +
      '}'
    );
  }

  function fetchVariantData(variantGids) {
    return fetch('https://' + shop + '/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query: buildQuery(), variables: { ids: variantGids } }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Storefront API HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (json.errors) throw new Error('Storefront API errors: ' + JSON.stringify(json.errors));
        var result = {};
        (json.data.nodes || []).forEach(function (node) {
          if (node && node.id) result[node.id] = node;
        });
        return result;
      });
  }

  // Classification
  function isShippable(variantData) {
    if (!variantData) return false;

    var title       = (variantData.product && variantData.product.title) || variantData.id;
    var pickupNodes = (variantData.storeAvailability && variantData.storeAvailability.nodes) || [];
    var pickupQty   = pickupNodes.reduce(function (sum, n) { return sum + (n.quantityAvailable || 0); }, 0);
    var totalQty    = variantData.quantityAvailable; // null when scope not granted

    if (typeof totalQty === 'number') {
      // Primary path: quantity math
      var warehouseQty = totalQty - pickupQty;
      var shippable    = warehouseQty > 0;
      
      return shippable;
    }

    var fallbackShippable = variantData.availableForSale === true && pickupNodes.length === 0;

    return fallbackShippable;
  }

  // Returns true if at least one store location has the item available for pickup,
  // regardless of reported quantity (handles warehouse-managed / pre-order stock).
  function hasPickupAvailability(variantData) {
    if (!variantData) return false;
    var pickupNodes = (variantData.storeAvailability && variantData.storeAvailability.nodes) || [];
    return pickupNodes.some(function (n) { return n.available === true; });
  }

  // Cart helpers
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('/cart.js ' + r.status);
      return r.json();
    });
  }

  function applyCartUpdates(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      if (!r.ok) throw new Error('/cart/update.js ' + r.status);
      return r.json();
    });
  }

  // Cart classification
  function classifyCart() {
    return getCart().then(function (cart) {
      if (!cart.items || cart.items.length === 0) return null;

      var seen = new Set();
      var gids = [];
      cart.items.forEach(function (item) {
        var gid = 'gid://shopify/ProductVariant/' + item.variant_id;
        if (!seen.has(gid)) { seen.add(gid); gids.push(gid); }
      });

      return fetchVariantData(gids).then(function (variantMap) {
        var hasShippingOnly = false;
        var hasPickupOnly   = false;
        var pickupItems     = [];

        cart.items.forEach(function (item) {
          var gid = 'gid://shopify/ProductVariant/' + item.variant_id;
          var variantData = variantMap[gid];

          var shippable = isShippable(variantData);
          var pickable  = hasPickupAvailability(variantData);

          if (shippable && !pickable) {
            hasShippingOnly = true;
          }

          if (!shippable) {
            hasPickupOnly = true;
            pickupItems.push(item);
          }
        });

        if (!(hasShippingOnly && hasPickupOnly)) {
          return null;
        }

        return pickupItems;
      });
    });
  }

  // Notice helpers
  function escHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function getNoticeEl() { return document.getElementById('cart-normalizer-notice'); }

  function showWarning(pickupItems) {
    var el = getNoticeEl();
    if (!el) return;

    var kachingFooter = document.querySelector('.kaching-cart__footer');
    if (kachingFooter && !kachingFooter.contains(el)) {
      kachingFooter.insertBefore(el, kachingFooter.firstChild);
    }

    var seenTitles = new Set();
    var titles     = [];
    pickupItems.forEach(function (item) {
      if (!seenTitles.has(item.product_title)) {
        seenTitles.add(item.product_title);
        titles.push(item.product_title);
      }
    });

    var listEl = el.querySelector('[data-items-list]');
    if (listEl) {
      listEl.innerHTML = titles.map(function (t) { return '<li>' + escHtml(t) + '</li>'; }).join('');
    }

    var confirmBtn = el.querySelector('[data-cart-normalizer-confirm]');
    if (confirmBtn) {
      confirmBtn.disabled    = false;
      confirmBtn.textContent = confirmBtn.dataset.labelDefault || confirmBtn.textContent;
    }

    el.hidden = false;
    setTimeout(function () {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }

  function hideNotice() {
    var el = getNoticeEl();
    if (el) el.hidden = true;
  }

  // Confirm: remove pickup-only items -> /checkout

  function handleConfirm() {
    if (!pendingPickupItems) return;

    var el         = getNoticeEl();
    var confirmBtn = el && el.querySelector('[data-cart-normalizer-confirm]');
    var cancelBtn  = el && el.querySelector('[data-cart-normalizer-cancel]');

    if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = 'Kraunama\u2026'; }
    if (cancelBtn)  { cancelBtn.disabled  = true; }

    var updates        = {};
    var itemsToProcess = pendingPickupItems;
    pendingPickupItems = null;

    itemsToProcess.forEach(function (item) { updates[item.key] = 0; });

    applyCartUpdates(updates)
      .catch(function (err) {
        console.error(err);
      })
      .then(function () {
        window.location.href = '/checkout';
      });
  }

  // Checkout interception
  function runCheckoutIntercept(event, onProceed) {
    event.preventDefault();
    event.stopImmediatePropagation();

    classifyCart()
      .then(function (pickupItems) {
        if (!pickupItems) {
          onProceed();
          return;
        }
        pendingPickupItems = pickupItems;
        showWarning(pickupItems);
      })
      .catch(function (err) {
        onProceed(); // never block the customer
      });
  }

  // Form submit (type="submit" checkout buttons on /cart page)
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.querySelector('[name="checkout"]')) return;
      if (form._cartNormalizerBusy) return;
      form._cartNormalizerBusy = true;

      var checkoutBtn = form.querySelector('[name="checkout"]');
      if (checkoutBtn) checkoutBtn.disabled = true;

      runCheckoutIntercept(event, function () {
        if (checkoutBtn) checkoutBtn.disabled = false;
        form._cartNormalizerBusy = false;
        form.submit();
      });
    },
    true
  );

  // Click intercept for type="button" checkout buttons (Kaching drawer button)
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest('button[name="checkout"]');
      if (!btn) return;
      if (btn.type === 'submit') return;
      if (btn._cartNormalizerBusy) return;
      btn._cartNormalizerBusy = true;
      btn.disabled = true;

      runCheckoutIntercept(event, function () {
        btn.disabled = false;
        btn._cartNormalizerBusy = false;
        window.location.href = '/checkout';
      });
    },
    true
  );

  // Notice button clicks
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-normalizer-confirm]')) {
      event.preventDefault();
      handleConfirm();
      return;
    }
    if (event.target.closest('[data-cart-normalizer-cancel]')) {
      event.preventDefault();
      pendingPickupItems = null;
      hideNotice();
    }
  });

  // Public API
  window.CartNormalizer = { check: classifyCart };
})();
